import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Plus, TrendingUp, Download, Upload, RefreshCw } from 'lucide-react';

// Configure API URL - change this to your server's address
const API_URL = 'https://pt.miasma.com/api';

const HealthTracker = () => {
  const [view, setView] = useState('history');
  const [entries, setEntries] = useState([]);
  const [editingDate, setEditingDate] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 7;

  const formatDateWithDay = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = days[date.getDay()];
  return `${dateStr} ${dayName}`;
  };

  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const weatherOptions = [
    { emoji: '☀️', label: 'Sunny' },
    { emoji: '⛅', label: 'Partly Cloudy' },
    { emoji: '☁️', label: 'Cloudy' },
    { emoji: '🌧️', label: 'Rainy' },
    { emoji: '⛈️', label: 'Stormy' },
    { emoji: '❄️', label: 'Snow' },
    { emoji: '🌨️', label: 'Snowy' },
    { emoji: '🌫️', label: 'Foggy' },
    { emoji: '🌬️', label: 'Windy' }
  ];

  const defaultEmotions = {
    Fear: ['Insignificant', 'Busy', 'Unsafe', 'Disoriented', 'Stressed', 'Worried', 'Insecure', 'Vulnerable', 'Imposter Syndrome', 'Overwhelmed', 'Panic', 'Jealous', 'Fixated', 'Anxious', 'Dread', 'Intimidated', 'Shame'],
    Sad: ['Lost', 'Lonely', 'Unmotivated', 'Bored', 'Tired', 'Loss', 'Disconnected', 'Disappointed', 'Directionless', 'Isolated', 'Burnt Out', 'Numb', 'Exhausted', 'Heartbroken', 'Depressed', 'Hurt', 'Grief'],
    Anger: ['Protective', 'Irritated', 'Tense', 'Skeptical', 'Defensive', 'Mad', 'Aggressive', 'Resentful', 'Loyal', 'Frustrated', 'Resistant', 'Distrust', 'Closed Off', 'Envious', 'Triggered', 'Hateful'],
    Genius: ['Alive', 'Interested', 'Like Myself', 'Unique', 'Focused', 'Creative', 'Confident', 'Challenged', 'Curious', 'Wonder', 'Brave', 'In Flow', 'Agency', 'Unafraid', 'Motivated', 'Intuitive', 'Courageous'],
    Joy: ['Connected', 'Present', 'Calm', 'Integrity', 'Safety', 'Care', 'Awe', 'Acceptance', 'Satisfied', 'Love', 'Belonging', 'Grateful', 'Ease', 'Dignity', 'Freedom', 'Silly'],
    Disgust: ['Not Good Enough', 'Embarrassed', 'Pity', 'Guilty', 'Uncomfortable', 'Avoidant', 'Self-Conscious', 'Ashamed', 'Humiliated', 'Judgmental', 'Aversion', 'Complicity', 'Nauseated', 'Awful', 'Unsettled']
  };

  const emotionColors = {
    Fear: '#9333ea',
    Sad: '#3b82f6',
    Anger: '#ef4444',
    Genius: '#f97316',
    Joy: '#eab308',
    Disgust: '#22c55e'
  };

  const [emotionWords, setEmotionWords] = useState(defaultEmotions);
  const [newWords, setNewWords] = useState({});
  
  const [currentEntry, setCurrentEntry] = useState({
    date: getLocalDate(),
    feelings: [],
    energy: 5,
    sleep: 7,
    steps: 0,
    weight: '',
    water: 0,
    coffee: 0,
    alcohol: 0,
    exercise: 0,
    standHours: 0,
    moveCalories: 0,
    exerciseSessions: [],
    timeOutside: 0,
    socialInteraction: 3,
    teethBrushed: false,
    showered: false,
    weather: [],
    travel: false,
    locations: ['Portland, Oregon'],
    meals: [],
    medications: {
      metoprolol: '50mg',
      bupropion: '300mg',
      prilosec: '40mg',
      vitaminD3: '2000IU',
      magnesium: '',
      zepboundMg: '',
      zepboundMl: ''
    },
    customMedications: [],
    notes: ''
  });

  // API functions
  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/entries`);
      if (!response.ok) throw new Error('Failed to fetch entries');
      const data = await response.json();
      setEntries(data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      setSaveStatus('Error loading entries. Please check your connection.');
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmotionWords = async () => {
    try {
      const response = await fetch(`${API_URL}/emotion-words`);
      if (!response.ok) throw new Error('Failed to fetch emotion words');
      const data = await response.json();
      if (Object.keys(data).length > 0) {
        setEmotionWords(data);
      }
    } catch (error) {
      console.error('Error fetching emotion words:', error);
    }
  };

  const saveEmotionWords = async (words) => {
    try {
      const response = await fetch(`${API_URL}/emotion-words`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(words)
      });
      if (!response.ok) throw new Error('Failed to save emotion words');
    } catch (error) {
      console.error('Error saving emotion words:', error);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchEmotionWords();
  }, []);

  const toggleFeeling = (feeling) => {
    const updated = currentEntry.feelings.includes(feeling)
      ? currentEntry.feelings.filter(f => f !== feeling)
      : [...currentEntry.feelings, feeling];
    setCurrentEntry({ ...currentEntry, feelings: updated });
  };

  const toggleExerciseSession = (session) => {
    const sessions = currentEntry.exerciseSessions || [];
    const updated = sessions.includes(session)
      ? sessions.filter(s => s !== session)
      : [...sessions, session];
    setCurrentEntry({ ...currentEntry, exerciseSessions: updated });
  };

  const toggleWeather = (weatherLabel) => {
    const updated = currentEntry.weather.includes(weatherLabel)
      ? currentEntry.weather.filter(w => w !== weatherLabel)
      : [...currentEntry.weather, weatherLabel];
    setCurrentEntry({ ...currentEntry, weather: updated });
  };

  const addLocation = () => {
    setCurrentEntry(prev => ({
      ...prev,
      locations: [...prev.locations, '']
    }));
  };

  const updateLocation = (index, value) => {
    const updated = [...currentEntry.locations];
    updated[index] = value;
    setCurrentEntry(prev => ({ ...prev, locations: updated }));
  };

  const removeLocation = (index) => {
    if (currentEntry.locations.length > 1) {
      setCurrentEntry(prev => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index)
      }));
    }
  };

  const addMeal = () => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    setCurrentEntry(prev => ({
      ...prev,
      meals: [...prev.meals, { time, description: '' }]
    }));
  };

  const updateMeal = (index, field, value) => {
    const updated = [...currentEntry.meals];
    updated[index][field] = value;
    setCurrentEntry(prev => ({ ...prev, meals: updated }));
  };

  const removeMeal = (index) => {
    setCurrentEntry(prev => ({
      ...prev,
      meals: prev.meals.filter((_, i) => i !== index)
    }));
  };

  const addNewWord = async (emotion) => {
    const word = newWords[emotion]?.trim();
    if (word) {
      const updated = {
        ...emotionWords,
        [emotion]: [...emotionWords[emotion], word]
      };
      setEmotionWords(updated);
      await saveEmotionWords(updated);
      setNewWords({ ...newWords, [emotion]: '' });
    }
  };

  const getEmotionCounts = (feelings) => {
    const counts = {};
    Object.keys(emotionColors).forEach(emotion => {
      counts[emotion] = 0;
    });
    
    feelings.forEach(feeling => {
      Object.keys(emotionWords).forEach(emotion => {
        if (emotionWords[emotion].includes(feeling)) {
          counts[emotion]++;
        }
      });
    });
    
    return counts;
  };

  const getEmotionData = (feelings) => {
    const counts = getEmotionCounts(feelings);
    return Object.entries(counts)
      .filter(([_, count]) => count > 0)
      .map(([emotion, count]) => ({
        name: emotion,
        value: count,
        color: emotionColors[emotion]
      }));
  };

  const getAverageEmotions = (entriesSubset) => {
    const totals = {};
    Object.keys(emotionColors).forEach(emotion => {
      totals[emotion] = 0;
    });
    
    entriesSubset.forEach(entry => {
      const counts = getEmotionCounts(entry.feelings || []);
      Object.keys(counts).forEach(emotion => {
        totals[emotion] += counts[emotion];
      });
    });
    
    return Object.entries(totals)
      .filter(([_, count]) => count > 0)
      .map(([emotion, count]) => ({
        name: emotion,
        value: count,
        color: emotionColors[emotion]
      }));
  };

  const handleSaveEntry = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${API_URL}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentEntry)
      });

      if (!response.ok) throw new Error('Failed to save entry');
      
      setSaveStatus('Entry saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
      
      // Refresh entries from server
      await fetchEntries();
      
      setEditingDate(null);
      setCurrentEntry({
        date: getLocalDate(),
        feelings: [],
        energy: 5,
        sleep: 7,
        steps: 0,
        weight: '',
        water: 0,
        coffee: 0,
        alcohol: 0,
        exercise: 0,
        standHours: 0,
        moveCalories: 0,
        exerciseSessions: [],
        timeOutside: 0,
        socialInteraction: 3,
        teethBrushed: false,
        showered: false,
        weather: [],
        travel: false,
        locations: ['Portland, Oregon'],
        meals: [],
        medications: {
          metoprolol: '50mg',
          bupropion: '300mg',
          prilosec: '40mg',
          vitaminD3: '2000IU',
          magnesium: '',
          zepboundMg: '',
          zepboundMl: ''
        },
        customMedications: [],
        notes: ''
      });
      
      setView('history');
    } catch (error) {
      console.error('Error saving entry:', error);
      setSaveStatus('Error saving: ' + error.message);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const exportData = async () => {
    try {
      const response = await fetch(`${API_URL}/export`);
      if (!response.ok) throw new Error('Failed to export data');
      const data = await response.json();
      
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `health-data-${getLocalDate()}.json`;
      link.click();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  const importData = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const imported = JSON.parse(event.target.result);
          
          const response = await fetch(`${API_URL}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(imported)
          });

          if (!response.ok) throw new Error('Failed to import data');
          
          await fetchEntries();
          await fetchEmotionWords();
          alert('Data imported successfully!');
        } catch (err) {
          console.error('Error importing data:', err);
          alert('Error importing data');
        }
      };
      reader.readAsText(file);
    }
  };

  const loadEntry = (date) => {
    const entry = entries.find(e => e.date === date);
    if (entry) {
      const loadedEntry = {
        ...entry,
        locations: entry.locations || ['Portland, Oregon'],
        travel: entry.travel || false,
        teethBrushed: entry.teethBrushed !== undefined ? entry.teethBrushed : (entry.teethBrushing > 0),
        showered: entry.showered || false,
        weight: entry.weight || '',
        meals: Array.isArray(entry.meals) ? entry.meals : [],
        standHours: entry.standHours || 0,
        moveCalories: entry.moveCalories || 0,
        exerciseSessions: entry.exerciseSessions || []
      };
      setCurrentEntry(loadedEntry);
      setEditingDate(date);
      setView('entry');
    }
  };

  const startNewEntry = () => {
    setEditingDate(null);
    setCurrentEntry({
      date: getLocalDate(),
      feelings: [],
      energy: 5,
      sleep: 7,
      steps: 0,
      weight: '',
      water: 0,
      coffee: 0,
      alcohol: 0,
      exercise: 0,
      standHours: 0,
      moveCalories: 0,
      exerciseSessions: [],
      timeOutside: 0,
      socialInteraction: 3,
      teethBrushed: false,
      showered: false,
      weather: [],
      travel: false,
      locations: ['Portland, Oregon'],
      meals: [],
      medications: {
        metoprolol: '50mg',
        bupropion: '300mg',
        prilosec: '40mg',
        vitaminD3: '2000IU',
        magnesium: '',
        zepboundMg: '',
        zepboundMl: ''
      },
      customMedications: [],
      notes: ''
    });
    setView('entry');
  };

  const updateMedication = (medName, value) => {
    setCurrentEntry({
      ...currentEntry,
      medications: {
        ...currentEntry.medications,
        [medName]: value
      }
    });
  };

  const addCustomMedication = () => {
    setCurrentEntry({
      ...currentEntry,
      customMedications: [...currentEntry.customMedications, { name: '', amount: '' }]
    });
  };

  const updateCustomMedication = (index, field, value) => {
    const updated = [...currentEntry.customMedications];
    updated[index][field] = value;
    setCurrentEntry({
      ...currentEntry,
      customMedications: updated
    });
  };

  const removeCustomMedication = (index) => {
    setCurrentEntry({
      ...currentEntry,
      customMedications: currentEntry.customMedications.filter((_, i) => i !== index)
    });
  };

  const calculateCorrelation = (var1, var2) => {
    const validEntries = entries.filter(e => {
      const a = e[var1];
      const b = e[var2];
      return a !== null && a !== undefined && b !== null && b !== undefined && !Number.isNaN(Number(a)) && !Number.isNaN(Number(b));
    });
    if (validEntries.length < 2) return null;
    
    const n = validEntries.length;
    const sum1 = validEntries.reduce((s, e) => s + parseFloat(e[var1]), 0);
    const sum2 = validEntries.reduce((s, e) => s + parseFloat(e[var2]), 0);
    const sum1Sq = validEntries.reduce((s, e) => s + parseFloat(e[var1]) ** 2, 0);
    const sum2Sq = validEntries.reduce((s, e) => s + parseFloat(e[var2]) ** 2, 0);
    const pSum = validEntries.reduce((s, e) => s + parseFloat(e[var1]) * parseFloat(e[var2]), 0);
    
    const num = pSum - (sum1 * sum2 / n);
    const den = Math.sqrt((sum1Sq - sum1 ** 2 / n) * (sum2Sq - sum2 ** 2 / n));
    
    return den === 0 ? 0 : num / den;
  };

  const getCorrelations = () => {
    const variables = ['energy', 'sleep', 'steps', 'water', 'coffee', 'alcohol', 'exercise', 'timeOutside', 'socialInteraction', 'standHours', 'moveCalories', 'weight'];
    const correlations = [];
    
    for (let i = 0; i < variables.length; i++) {
      for (let j = i + 1; j < variables.length; j++) {
        const corr = calculateCorrelation(variables[i], variables[j]);
        if (corr !== null) {
          correlations.push({
            var1: variables[i],
            var2: variables[j],
            correlation: corr
          });
        }
      }
    }
    
    return correlations.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-gray-700">Loading your health data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <header className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-indigo-900">Personal Health & Habit Tracker</h1>
            {syncing && <RefreshCw className="w-5 h-5 animate-spin text-indigo-600" />}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setView('history')} className={`px-4 py-2 rounded-lg font-medium ${view === 'history' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              All Entries
            </button>
            <button onClick={() => setView('trends')} className={`px-4 py-2 rounded-lg font-medium ${view === 'trends' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              <TrendingUp className="inline w-4 h-4 mr-2" />
              Trends
            </button>
            <button onClick={() => setView('correlations')} className={`px-4 py-2 rounded-lg font-medium ${view === 'correlations' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
              Correlations
            </button>
            <button onClick={startNewEntry} className="px-4 py-2 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 ml-auto">
              <Plus className="inline w-4 h-4 mr-2" />
              New Entry
            </button>
            <button onClick={exportData} className="px-4 py-2 rounded-lg font-medium bg-green-600 text-white">
              <Download className="inline w-4 h-4 mr-2" />
              Export
            </button>
            <label className="px-4 py-2 rounded-lg font-medium bg-blue-600 text-white cursor-pointer">
              <Upload className="inline w-4 h-4 mr-2" />
              Import
              <input type="file" accept=".json" onChange={importData} className="hidden" />
            </label>
          </div>
        </header>

        {view === 'history' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">All Entries</h2>
              {entries.length === 0 ? (
                <p className="text-gray-600">No entries yet. Click "New Entry" to get started!</p>
              ) : (
                <>
                  <div className="space-y-3">
                {(() => {
                  const reversedEntries = entries.slice().reverse();
                  const indexOfLastEntry = currentPage * entriesPerPage;
                  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
                  const currentEntries = reversedEntries.slice(indexOfFirstEntry, indexOfLastEntry);

                  return currentEntries.map((entry, idx) => (
                  <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => loadEntry(entry.date)}>
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-lg">{formatDateWithDay(entry.date)}</h3>
                      <button className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                        Edit
                      </button>
                    </div>
                    
                    {entry.feelings && entry.feelings.length > 0 && (
                      <div className="mb-3">
                        <span className="text-xs font-medium text-gray-500 uppercase">Feelings</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(() => {
                            const emotionOrder = ['Joy', 'Genius', 'Anger', 'Fear', 'Sad', 'Disgust'];
                            const sortedFeelings = [...entry.feelings].sort((a, b) => {
                              const emotionA = Object.keys(emotionWords).find(e => emotionWords[e].includes(a));
                              const emotionB = Object.keys(emotionWords).find(e => emotionWords[e].includes(b));
                              const orderA = emotionOrder.indexOf(emotionA);
                              const orderB = emotionOrder.indexOf(emotionB);
                              if (orderA !== orderB) return orderA - orderB;
                              return a.localeCompare(b);
                            });
                            return sortedFeelings.map(feeling => {
                              const emotion = Object.keys(emotionWords).find(e => emotionWords[e].includes(feeling));
                              return (
                                <span
                                  key={feeling}
                                  className="px-2 py-1 rounded-full text-white text-xs"
                                  style={{ backgroundColor: emotionColors[emotion] }}
                                >
                                  {feeling}
                                </span>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600">
                      {entry.energy && <div><span className="font-medium">Energy:</span> {entry.energy}</div>}
                      {entry.sleep && <div><span className="font-medium">Sleep:</span> {entry.sleep}h</div>}
                      {entry.steps > 0 && <div><span className="font-medium">Steps:</span> {entry.steps}</div>}
                      {entry.weight && <div><span className="font-medium">Weight:</span> {entry.weight} lbs</div>}
                      {entry.exercise > 0 && <div><span className="font-medium">Exercise:</span> {entry.exercise}m</div>}
                      {entry.timeOutside > 0 && <div><span className="font-medium">Time Outside:</span> {entry.timeOutside}m</div>}
                      {entry.socialInteraction && <div><span className="font-medium">Social:</span> {entry.socialInteraction}/10</div>}
                      {entry.teethBrushed && <div><span className="font-medium">Teeth Brushed:</span> Yes</div>}
                      {entry.showered && <div><span className="font-medium">Showered:</span> Yes</div>}
                      {entry.water > 0 && <div><span className="font-medium">Water:</span> {entry.water}oz</div>}
                      {entry.coffee > 0 && <div><span className="font-medium">Coffee:</span> {entry.coffee}oz</div>}
                      {entry.alcohol > 0 && <div><span className="font-medium">Alcohol:</span> {entry.alcohol}oz</div>}
                      {entry.standHours > 0 && <div><span className="font-medium">Stand:</span> {entry.standHours}h</div>}
                      {entry.moveCalories > 0 && <div><span className="font-medium">Move:</span> {entry.moveCalories}cal</div>}
                    </div>
                    
                    {entry.exerciseSessions && entry.exerciseSessions.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium text-gray-600">Sessions:</span> {entry.exerciseSessions.join(', ')}
                      </div>
                    )}
                    
                    {entry.weather && entry.weather.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium text-gray-600">Weather:</span> {entry.weather.join(', ')}
                      </div>
                    )}
                    
                    {entry.locations && entry.locations.length > 0 && entry.locations[0] && (
                      <div className="mt-1 text-sm">
                        <span className="font-medium text-gray-600">Location:</span> {entry.locations.join(' → ')}
                        {entry.travel && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Travel</span>}
                      </div>
                    )}
                    
                    {entry.meals && Array.isArray(entry.meals) && entry.meals.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Meals</span>
                        <div className="mt-1 space-y-1">
                          {entry.meals.map((meal, i) => (
                            <div key={i} className="text-sm text-gray-600">
                              <span className="font-medium">{meal.time}:</span> {meal.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {entry.notes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        <span className="font-medium">Notes:</span> {entry.notes}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>

            {/* Pagination Controls */}
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(entries.length / entriesPerPage)))}
                disabled={currentPage === Math.ceil(entries.length / entriesPerPage)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Previous Week
              </button>
              <span className="text-gray-600">
                Page {currentPage} of {Math.ceil(entries.length / entriesPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Next Week
              </button>
              
            </div>
          </>
        )}
          </div>
        )}

        {/* The rest of the views (trends, entry form, correlations) remain the same as your original code */}
        {/* Due to character limits, I'll include a note that these sections are identical */}
        {view === 'trends' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Emotional Trends</h2>
            {entries.length < 1 ? (
              <p className="text-gray-600">Add entries to see trends</p>
            ) : (
              <div className="space-y-8">
                {entries.length >= 7 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Average Emotional Composition (Last 7 Days)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={getAverageEmotions(entries.slice(-7))}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {getAverageEmotions(entries.slice(-7)).map((item, i) => (
                            <Cell key={i} fill={item.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold mb-2">Activity & Sleep</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={entries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="sleep" stroke="#10b981" strokeWidth={2} />
                      <Line type="monotone" dataKey="exercise" stroke="#f59e0b" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Weight</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={entries.filter(e => e.weight)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={['dataMin - 15', 'dataMax + 5']} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="weight" stroke="#8b5cf6" strokeWidth={2} name="Weight (lbs)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">So Much Winning</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={entries.map(entry => {
                      const energyScore = Number(entry.energy || 0);
                      const sleepScore = Number(entry.sleep || 0);
                      const showeredScore = entry.showered ? 1 : -1;
                      const exerciseScore = Math.floor(Number(entry.exercise || 0) / 10);
                      const outsideScore = Math.floor(Number(entry.timeOutside || 0) / 30);
                      const socialScore = Number(entry.socialInteraction || 0);
                      const travelScore = entry.travel ? -1 : 0;
                      const totalScore = energyScore + sleepScore + showeredScore + exerciseScore + outsideScore + socialScore + travelScore;
                      
                      return {
                        date: entry.date,
                        score: totalScore
                      };
                    })}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 'auto']} />
                      <Tooltip content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const entry = entries.find(e => e.date === data.date);
                          if (!entry) return null;
                          
                          const energyScore = Number(entry.energy || 0);
                          const sleepScore = Number(entry.sleep || 0);
                          const showeredScore = entry.showered ? 1 : -1;
                          const exerciseScore = Math.floor(Number(entry.exercise || 0) / 10);
                          const outsideScore = Math.floor(Number(entry.timeOutside || 0) / 30);
                          const socialScore = Number(entry.socialInteraction || 0);
                          const travelScore = entry.travel ? -1 : 0;
                          
                          return (
                            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                              <p className="font-semibold mb-1">{data.date}</p>
                              <p className="text-lg font-bold text-red-600 mb-2">Score: {payload[0].value}</p>
                              <div className="text-xs text-gray-600 space-y-0.5">
                                <div>Energy: {energyScore}</div>
                                <div>Sleep: {sleepScore}</div>
                                <div>Shower: {showeredScore}</div>
                                <div>Exercise: {exerciseScore}</div>
                                <div>Outside: {outsideScore}</div>
                                <div>Social: {socialScore}</div>
                                <div>Travel: {travelScore}</div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }} />
                      <Legend />
                      <Line type="monotone" dataKey="score" stroke="#ef4444" strokeWidth={2} name="So Much Winning" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Daily Emotional Composition</h3>
                  {(() => {
                    const today = new Date();
                    const currentWeekStart = new Date(today);
                    currentWeekStart.setDate(today.getDate() - today.getDay());
                    
                    const startDate = new Date(currentWeekStart);
                    startDate.setDate(startDate.getDate() - 14);
                    
                    const weeks = [];
                    for (let w = 0; w < 5; w++) {
                      const week = [];
                      for (let d = 0; d < 7; d++) {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + (w * 7) + d);
                        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const entry = entries.find(e => e.date === dateStr);
                        week.push({ date, dateStr, entry, day: date.getDate() });
                      }
                      weeks.push(week);
                    }

                    return (
                      <div>
                        <div className="grid grid-cols-8 gap-1 mb-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Week'].map(day => (
                            <div key={day} className="text-center text-xs font-semibold text-gray-600 p-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        {weeks.map((week, weekIdx) => (
                          <div key={weekIdx} className="grid grid-cols-8 gap-1 mb-1">
                            {week.map((dayData, dayIdx) => {
                              const emotionData = dayData.entry ? getEmotionData(dayData.entry.feelings || []) : [];
                              const isToday = dayData.dateStr === today.toISOString().split('T')[0];
                              
                              return (
                                <div
                                  key={dayIdx}
                                  className={`aspect-square border rounded p-1 relative ${isToday ? 'ring-2 ring-indigo-500' : ''}`}
                                  title={dayData.dateStr}
                                >
                                  <div className="flex justify-between items-start mb-1">
                                    <div className="text-xs text-gray-600">{dayData.day}</div>
                                    {dayData.entry?.weather && dayData.entry.weather.length > 0 && (
                                      <div className="text-xl">
                                        {weatherOptions.find(w => w.label === dayData.entry.weather[0])?.emoji || ''}
                                      </div>
                                    )}
                                  </div>
                                  {emotionData.length > 0 && (
                                    <div className="w-full h-16">
                                      <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                          <Pie
                                            data={emotionData}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius="100%"
                                            stroke="none"
                                          >
                                            {emotionData.map((item, i) => (
                                              <Cell key={i} fill={item.color} />
                                            ))}
                                          </Pie>
                                        </PieChart>
                                      </ResponsiveContainer>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* Weekly aggregate pie chart */}
                            <div
                              className="aspect-square border-2 border-indigo-400 rounded p-1 relative bg-indigo-50"
                              title={`Week ${weekIdx + 1} aggregate`}
                            >
                              <div className="text-xs text-indigo-700 font-semibold mb-1">W{weekIdx + 1}</div>
                              {(() => {
                                const weekEntries = week.filter(day => day.entry).map(day => day.entry);
                                const weekEmotionData = weekEntries.length > 0 ? getAverageEmotions(weekEntries) : [];
                                return weekEmotionData.length > 0 && (
                                  <div className="w-full h-16">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <Pie
                                          data={weekEmotionData}
                                          dataKey="value"
                                          cx="50%"
                                          cy="50%"
                                          outerRadius="100%"
                                          stroke="none"
                                        >
                                          {weekEmotionData.map((item, i) => (
                                            <Cell key={i} fill={item.color} />
                                          ))}
                                        </Pie>
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        ))}        
                        {/* Daily aggregate row */}
                        <div className="grid grid-cols-8 gap-1 mt-2 pt-2 border-t-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, dayIdx) => {
                            const dayEntries = weeks
                              .map(week => week[dayIdx])
                              .filter(day => day.entry)
                              .map(day => day.entry);
                            
                            const dayEmotionData = dayEntries.length > 0 ? getAverageEmotions(dayEntries) : [];
                            
                            return (
                              <div
                                key={dayName}
                                className="aspect-square border-2 border-green-400 rounded p-1 relative bg-green-50"
                                title={`${dayName} aggregate across all weeks`}
                              >
                                <div className="text-xs text-green-700 font-semibold mb-1">{dayName}</div>
                                {dayEmotionData.length > 0 && (
                                  <div className="w-full h-16">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <PieChart>
                                        <Pie
                                          data={dayEmotionData}
                                          dataKey="value"
                                          cx="50%"
                                          cy="50%"
                                          outerRadius="100%"
                                          stroke="none"
                                        >
                                          {dayEmotionData.map((item, i) => (
                                            <Cell key={i} fill={item.color} />
                                          ))}
                                        </Pie>
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Empty cell in bottom-right corner */}
                          <div className="aspect-square border-2 border-gray-300 rounded p-1 bg-gray-100"></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'entry' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Entry form - keeping all your existing form fields */}
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {editingDate ? `Editing ${editingDate}` : 'New Entry'}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={currentEntry.date}
                onChange={(e) => setCurrentEntry({ ...currentEntry, date: e.target.value })}
                className="w-full p-2 border rounded"
              />
            </div>

            <h3 className="text-xl font-bold text-gray-800 mb-4">Daily Metrics</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Energy (1-10)</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={currentEntry.energy}
                  onChange={(e) => setCurrentEntry(prev => ({ ...prev, energy: Number(e.target.value) }))}
                  className="w-full"
                />
                <span className="text-sm text-gray-600">{currentEntry.energy}</span>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Sleep (hours)</label>
                <input
                  type="number"
                  step="0.5"
                  value={currentEntry.sleep}
                  onChange={(e) => setCurrentEntry(prev => ({ ...prev, sleep: Number(e.target.value) }))}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Weight (lbs)</label>
                <input
                  type="number"
                  step="0.1"
                  value={currentEntry.weight}
                  onChange={(e) => setCurrentEntry(prev => ({ ...prev, weight: Number(e.target.value) }))}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={currentEntry.showered}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, showered: e.target.checked }))}
                    className="mr-2 w-4 h-4"
                  />
                  <span className="text-sm font-medium">Showered</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={currentEntry.teethBrushed}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, teethBrushed: e.target.checked }))}
                    className="mr-2 w-4 h-4"
                  />
                  <span className="text-sm font-medium">Teeth Brushed</span>
                </label>
              </div>
            </div>

            {/* Continue with all your existing form sections... I'll keep them condensed for space */}
            {/* Activity, Environment, Food & Drink, Medications, Feelings sections remain identical */}
            
            <div className="border-t pt-4 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Activity</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-1">Steps</label>
                  <input
                    type="number"
                    value={currentEntry.steps}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, steps: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Stand (hours)</label>
                  <input
                    type="number"
                    value={currentEntry.standHours}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, standHours: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Move (calories)</label>
                  <input
                    type="number"
                    value={currentEntry.moveCalories}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, moveCalories: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Exercise (minutes)</label>
                  <input
                    type="number"
                    value={currentEntry.exercise}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, exercise: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Exercise Sessions</label>
                  <div className="flex flex-wrap gap-2">
                    {['Outdoor Walk', 'Indoor Walk', 'Elliptical', 'Peloton', 'Stretching', 'Yoga', 'Strength Snack'].map(session => (
                      <button
                        key={session}
                        type="button"
                        onClick={() => toggleExerciseSession(session)}
                        className={`px-3 py-1 rounded-full text-sm transition-all ${
                          (currentEntry.exerciseSessions || []).includes(session)
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {session}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Environment</h3>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Weather (select all that apply)</label>
                  <div className="flex flex-wrap gap-2">
                    {weatherOptions.map(option => (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => toggleWeather(option.label)}
                        className={`px-2 py-1 rounded-lg text-2xl transition-all ${
                          currentEntry.weather.includes(option.label)
                            ? 'bg-indigo-600 shadow-md ring-2 ring-indigo-300'
                            : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                        title={option.label}
                      >
                        {option.emoji}
                      </button>
                    ))}
                  </div>
                  {currentEntry.weather.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">{currentEntry.weather.join(', ')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Time Outside (minutes)</label>
                  <input
                    type="number"
                    value={currentEntry.timeOutside}
                    onFocus={(e) => e.target.value === '0' && e.target.select()}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, timeOutside: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Social Interaction (1-10)</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={currentEntry.socialInteraction}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, socialInteraction: Number(e.target.value) }))}
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600">{currentEntry.socialInteraction}</span>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={currentEntry.travel}
                      onChange={(e) => setCurrentEntry(prev => ({ ...prev, travel: e.target.checked }))}
                      className="mr-2 w-4 h-4"
                    />
                    <span className="font-medium">Travel</span>
                  </label>
                  <label className="block text-sm font-medium mb-2">Location(s)</label>
                  <div className="space-y-2">
                    {(currentEntry.locations || ['Portland, Oregon']).map((location, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => updateLocation(idx, e.target.value)}
                          className="flex-1 p-2 border rounded"
                          placeholder="Location"
                        />
                        {(currentEntry.locations || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLocation(idx)}
                            className="text-red-600 hover:text-red-800 text-sm px-3"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addLocation}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      + Add location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Food & Drink</h3>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-1">Water (ounces)</label>
                  <input
                    type="number"
                    value={currentEntry.water}
                    onFocus={(e) => e.target.value === '0' && e.target.select()}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, water: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Coffee/Tea (ounces)</label>
                  <input
                    type="number"
                    value={currentEntry.coffee}
                    onFocus={(e) => e.target.value === '0' && e.target.select()}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, coffee: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Alcohol (ounces)</label>
                  <input
                    type="number"
                    value={currentEntry.alcohol}
                    onFocus={(e) => e.target.value === '0' && e.target.select()}
                    onChange={(e) => setCurrentEntry(prev => ({ ...prev, alcohol: Number(e.target.value) }))}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Meals</label>
                  <div className="space-y-2">
                    {(Array.isArray(currentEntry.meals) ? currentEntry.meals : []).map((meal, idx) => (
                      <div key={idx} className="flex gap-2 items-start bg-gray-50 p-2 rounded">
                        <input
                          type="time"
                          step="900"
                          value={meal.time}
                          onChange={(e) => updateMeal(idx, 'time', e.target.value)}
                          className="w-36 p-2 border rounded text-sm"
                        />
                        <input
                          type="text"
                          value={meal.description}
                          onChange={(e) => updateMeal(idx, 'description', e.target.value)}
                          className="flex-1 p-2 border rounded text-sm"
                          placeholder="What did you eat?"
                        />
                        <button
                          type="button"
                          onClick={() => removeMeal(idx)}
                          className="text-red-600 hover:text-red-800 text-sm px-3"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addMeal}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      + Add meal
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Medications</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Metoprolol</span>
                  <input
                    type="text"
                    value={currentEntry.medications.metoprolol}
                    onChange={(e) => updateMedication('metoprolol', e.target.value)}
                    className="w-24 p-2 border rounded text-sm"
                    placeholder="50mg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Bupropion</span>
                  <input
                    type="text"
                    value={currentEntry.medications.bupropion}
                    onChange={(e) => updateMedication('bupropion', e.target.value)}
                    className="w-24 p-2 border rounded text-sm"
                    placeholder="300mg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Prilosec</span>
                  <input
                    type="text"
                    value={currentEntry.medications.prilosec}
                    onChange={(e) => updateMedication('prilosec', e.target.value)}
                    className="w-24 p-2 border rounded text-sm"
                    placeholder="40mg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Vitamin D3</span>
                  <input
                    type="text"
                    value={currentEntry.medications.vitaminD3}
                    onChange={(e) => updateMedication('vitaminD3', e.target.value)}
                    className="w-24 p-2 border rounded text-sm"
                    placeholder="2000IU"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Magnesium Oxide</span>
                  <input
                    type="text"
                    value={currentEntry.medications.magnesium}
                    onChange={(e) => updateMedication('magnesium', e.target.value)}
                    className="w-24 p-2 border rounded text-sm"
                    placeholder="400mg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-32 text-sm">Zepbound</span>
                  <input
                    type="text"
                    value={currentEntry.medications.zepboundMg}
                    onChange={(e) => updateMedication('zepboundMg', e.target.value)}
                    className="w-16 p-2 border rounded text-sm"
                    placeholder="mg"
                  />
                  <span className="text-sm text-gray-600">mg at</span>
                  <input
                    type="text"
                    value={currentEntry.medications.zepboundMl}
                    onChange={(e) => updateMedication('zepboundMl', e.target.value)}
                    className="w-16 p-2 border rounded text-sm"
                    placeholder="mL"
                  />
                  <span className="text-sm text-gray-600">mL</span>
                </div>
                
                {currentEntry.customMedications.map((med, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <input
                      type="text"
                      value={med.name}
                      onChange={(e) => updateCustomMedication(idx, 'name', e.target.value)}
                      className="w-32 p-2 border rounded text-sm"
                      placeholder="Medication name"
                    />
                    <input
                      type="text"
                      value={med.amount}
                      onChange={(e) => updateCustomMedication(idx, 'amount', e.target.value)}
                      className="w-24 p-2 border rounded text-sm"
                      placeholder="Amount"
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomMedication(idx)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={addCustomMedication}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  + Add medication
                </button>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">How Are You Feeling?</h3>
              
              {currentEntry.feelings.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-2">Today's Emotional Snapshot</h4>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const emotionOrder = ['Joy', 'Genius', 'Anger', 'Fear', 'Sad', 'Disgust'];
                      const sortedFeelings = [...currentEntry.feelings].sort((a, b) => {
                        const emotionA = Object.keys(emotionWords).find(e => emotionWords[e].includes(a));
                        const emotionB = Object.keys(emotionWords).find(e => emotionWords[e].includes(b));
                        const orderA = emotionOrder.indexOf(emotionA);
                        const orderB = emotionOrder.indexOf(emotionB);
                        if (orderA !== orderB) return orderA - orderB;
                        return a.localeCompare(b);
                      });
                      return sortedFeelings.map(feeling => {
                        const emotion = Object.keys(emotionWords).find(e => emotionWords[e].includes(feeling));
                        return (
                          <span
                            key={feeling}
                            className="px-3 py-1 rounded-full text-white text-sm"
                            style={{ backgroundColor: emotionColors[emotion] }}
                          >
                            {feeling}
                          </span>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {Object.entries(emotionWords)
                .sort((a, b) => {
                  const order = ['Joy', 'Genius', 'Anger', 'Fear', 'Sad', 'Disgust'];
                  return order.indexOf(a[0]) - order.indexOf(b[0]);
                })
                .map(([emotion, words]) => (
                <div key={emotion} className="mb-6">
                  <h4 className="text-base font-semibold mb-3" style={{ color: emotionColors[emotion] }}>
                    {emotion}
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {words.map(word => {
  const isCustomWord = !defaultEmotions[emotion].includes(word);
  
  return (
    <div key={word} className="relative group">
      <button
        type="button"
        onClick={() => toggleFeeling(word)}
        className={`px-3 py-1 rounded-full text-sm transition-all ${
          currentEntry.feelings.includes(word)
            ? 'text-white shadow-md'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${isCustomWord ? 'pr-8' : ''}`}
        style={currentEntry.feelings.includes(word) ? { backgroundColor: emotionColors[emotion] } : {}}
      >
        {word}
      </button>
      {isCustomWord && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            if (window.confirm(`Remove "${word}" from ${emotion}?`)) {
              const updated = {
                ...emotionWords,
                [emotion]: emotionWords[emotion].filter(w => w !== word)
              };
              setEmotionWords(updated);
              await saveEmotionWords(updated);
              if (currentEntry.feelings.includes(word)) {
                setCurrentEntry({
                  ...currentEntry,
                  feelings: currentEntry.feelings.filter(f => f !== word)
                });
              }
            }
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity hover:bg-red-600"
          title={`Remove ${word}`}
        >
          ×
        </button>
      )}
    </div>
  );
})}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add new feeling word..."
                      value={newWords[emotion] || ''}
                      onChange={(e) => setNewWords({ ...newWords, [emotion]: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && addNewWord(emotion)}
                      className="flex-1 p-2 border rounded text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => addNewWord(emotion)}
                      className="px-4 py-2 rounded text-white text-sm"
                      style={{ backgroundColor: emotionColors[emotion] }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={currentEntry.notes}
                onChange={(e) => setCurrentEntry({ ...currentEntry, notes: e.target.value })}
                className="w-full p-2 border rounded"
                rows="3"
                placeholder="Any other observations?"
              />
            </div>

            <button 
              type="button"
              onClick={handleSaveEntry}
              disabled={syncing}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {syncing ? (
                <>
                  <RefreshCw className="inline w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="inline w-5 h-5 mr-2" />
                  Save Entry
                </>
              )}
            </button>
            
            <button 
              type="button"
              onClick={() => setView('history')}
              className="mt-2 w-full bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400"
            >
              Cancel
            </button>

            {saveStatus && (
              <div className={`mt-4 p-3 rounded-lg text-center font-medium ${
                saveStatus.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {saveStatus}
              </div>
            )}
          </div>
        )}

        {view === 'correlations' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Correlation Analysis</h2>
            {entries.length < 3 ? (
              <p className="text-gray-600">Add at least 3 entries to see correlations</p>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Correlation values range from -1 to 1. Values closer to 1 or -1 indicate stronger relationships.
                </p>
                <div className="space-y-2">
                  {getCorrelations().slice(0, 15).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <span className="font-medium">
                        {item.var1} ↔ {item.var2}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${item.correlation > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.abs(item.correlation) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-mono w-16 text-right">
                          {item.correlation.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthTracker;
